import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Alert,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { format } from 'date-fns';
import { approveReservation, notifyRearrange } from '@services/reservationService';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MessageIcon from '@mui/icons-material/Message';
import ConversationPage from '@components/chatbox/ConversationPage';
import CloseIcon from '@mui/icons-material/Close';
import NotifyRearrangeDialog from './modals/NotifyRearrangeDialog';
import { displayPhoneNumber } from '../../utils/phoneUtils';
import './AwaitingApprovalList.scss';

interface ReservationUser {
  _id: string;
  email?: string;
  displayName?: string;
  phone?: string;
  [key: string]: unknown;
}

interface ReservationPlan {
  _id: string;
  name: string;
  zh_name?: string;
  [key: string]: unknown;
}

interface Reservation {
  _id: string;
  customerName?: string;
  start: string;
  end: string;
  userId?: ReservationUser;
  planId?: ReservationPlan;
  staffId: string | { _id: string; [key: string]: unknown };
  status: string;
  createdAt?: string;
  [key: string]: unknown;
}

interface AwaitingApprovalListProps {
  reservations: Reservation[];
  onRefresh: () => void;
}

const AwaitingApprovalList = ({ reservations, onRefresh }: AwaitingApprovalListProps) => {
  const { t, i18n } = useTranslation();
  const [showRearrangeDialog, setShowRearrangeDialog] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);

  const handleApprove = async (reservationId: string) => {
    try {
      setError(null);
      await approveReservation(reservationId);
      onRefresh();
    } catch (error) {
      console.error('Error approving reservation:', error);
      setError(t('reservationsPage.errorApprovingReservation'));
    }
  };

  const handleRearrangeClick = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setShowNotifyDialog(true);
  };

  const handleCloseDialog = () => {
    setShowRearrangeDialog(false);
    setSelectedReservation(null);
  };

  const handleCloseNotifyDialog = () => {
    setShowNotifyDialog(false);
    setSelectedReservation(null);
  };

  const handleConfirmNotify = async () => {
    if (!selectedReservation) return;
    setIsNotifying(true);
    setError(null);
    try {
      await notifyRearrange(selectedReservation._id);
      setShowNotifyDialog(false);
      setShowRearrangeDialog(true);
    } catch (error) {
      console.error('Error notifying for rearrangement:', error);
      setError(t('reservationsPage.errorNotifyingRearrange', 'Failed to send notification.'));
    } finally {
      setIsNotifying(false);
    }
  };

  if (reservations.length === 0) {
    return null;
  }

  return (
    <>
      <Paper
        elevation={2}
        className="awaiting-approval-paper"
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <Typography variant="h6" color="primary">
            {t('reservationsPage.awaitingApproval')}
          </Typography>
          <Chip
            label={reservations.length}
            color="primary"
            size="small"
            className="pending-count-chip"
          />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {reservations.map((reservation) => (
            <Box key={reservation._id} className="reservation-item">
              <Box className="reservation-layout">
                <Box className="info-section">
                  <Stack spacing={1.5} className="reservation-info">
                    <Box className="info-row">
                      <PersonIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="medium" noWrap>
                        {reservation.customerName || ''}
                      </Typography>
                    </Box>

                    <Box className="info-row">
                      <AccessTimeIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {format(new Date(reservation.start), 'PPp')}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" noWrap>
                      {i18n.language === 'zh' ? reservation.planId?.zh_name : reservation.planId?.name}
                    </Typography>

                    <Box className="info-row">
                      <PhoneIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {displayPhoneNumber(reservation.userId?.phone || '', t('common.noPhone', 'No phone'))}
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {t('reservationsPage.created')} {reservation.createdAt ? formatDistanceToNow(new Date(reservation.createdAt), { addSuffix: true }) : ''}
                    </Typography>
                  </Stack>
                </Box>

                <Box className="action-section">
                  <Stack direction="row" spacing={1} className="action-buttons">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleApprove(reservation._id)}
                      startIcon={<CheckCircleOutlineIcon />}
                      className="approve-button"
                    >
                      {t('reservationsPage.approve')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleRearrangeClick(reservation)}
                      startIcon={<MessageIcon />}
                      className="rearrange-button"
                    >
                      {t('reservationsPage.contactToRearrange')}
                    </Button>
                  </Stack>
                </Box>
              </Box>
              <Divider className="reservation-divider" />
            </Box>
          ))}
        </Stack>
      </Paper>

      <NotifyRearrangeDialog
        open={showNotifyDialog}
        onClose={handleCloseNotifyDialog}
        onConfirm={handleConfirmNotify}
        isNotifying={isNotifying}
        hasUserPhone={!!selectedReservation?.userId?.phone}
      />

      <Dialog
        open={showRearrangeDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        className="rearrange-dialog"
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ position: 'relative' }}>
            <MessageIcon color="primary" />
            <Typography variant="h6">
              {t('reservationsPage.rearrangeReservation')} {selectedReservation && `(${selectedReservation?.userId?.displayName || selectedReservation?.userId?.email})`}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: -15,
                top: -5,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent className="conversation-dialog-content">
          {selectedReservation && (
            <ConversationPage userId={selectedReservation?.userId?._id} staffId={typeof selectedReservation?.staffId === 'string' ? selectedReservation.staffId : selectedReservation?.staffId?._id} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AwaitingApprovalList;
